import { dueTimeOf } from '@nedelnik/shared';

import { orderKeyBetweenTasks } from '../board/columns.js';

import type { TaskGroup } from './grouping.js';
import type { Placement } from '../task/landed.js';
import type { Task } from '@nedelnik/shared';

/**
 * Куда встаёт задача, брошенная в списке (3.1.11).
 *
 * Список весь — одна последовательность строк через все группы, и бросок
 * ставит задачу на место той, на которую её положили, как переставляет строки
 * сама анимация `@dnd-kit`: вниз — под неё, вверх — над ней. Группа — та, в
 * которой лежит строка-цель: так задача и меняет статус, переезжая из группы
 * в группу (решение владельца 2026-10-11, как на доске).
 *
 * Ручной порядок внутри группы подчинён сроку (3.6.6): задача встаёт среди
 * задач **со своим сроком**. Брошенная выше задач с более ранним сроком,
 * она останавливается первой в своём сроке; ниже более поздних — последней.
 * Иначе ключ порядка, посчитанный между соседями по экрану, не значил бы
 * ничего: сортировка всё равно поставила бы задачу по сроку, а среди своих она
 * оказалась бы где придётся.
 */
export function planListDrop(
  groups: readonly TaskGroup[],
  activeId: string,
  overId: string,
): Placement | undefined {
  const flat = groups.flatMap((group) => group.tasks.map((task) => ({ task, group })));
  const from = flat.findIndex((row) => row.task.id === activeId);
  const to = flat.findIndex((row) => row.task.id === overId);
  const active = flat[from];
  const over = flat[to];
  if (active === undefined || over === undefined || from === to) return undefined;

  // Строки, которые после броска окажутся выше задачи, — по ним считается её
  // место среди остальных задач целевой группы.
  const moved = flat.filter((row) => row.task.id !== activeId);
  moved.splice(to, 0, active);
  const status = over.group.status;
  const rest = over.group.tasks.filter((task) => task.id !== activeId);
  const index = moved
    .slice(0, to)
    .filter((row) => row.group.status === status && row.task.id !== activeId).length;

  return { status, order_key: keyAmongSameDue(active.task, rest, index) };
}

/** Срок, внутри которого действует ручной порядок: дата и время вместе. */
function dueBucket(task: Task): string {
  return `${task.due ?? ''}|${dueTimeOf(task) ?? ''}`;
}

/**
 * Ключ порядка для места `index` среди `rest`, сдвинутого в пределы своего
 * срока. Собственный ключ сохраняется, если он и так лежит между соседями:
 * задача, вставшая туда же, не порождает правку, которая ничего не меняет.
 */
function keyAmongSameDue(task: Task, rest: readonly Task[], index: number): string {
  const bucket = dueBucket(task);
  const first = rest.findIndex((item) => dueBucket(item) === bucket);
  if (first < 0) return task.order_key;

  let last = first;
  while (last < rest.length && dueBucket(rest[last] as Task) === bucket) last += 1;

  const at = Math.min(Math.max(index, first), last);
  const before = at > first ? rest[at - 1] : undefined;
  const after = at < last ? rest[at] : undefined;

  const fits =
    (before === undefined || before.order_key < task.order_key) &&
    (after === undefined || task.order_key < after.order_key);
  return fits ? task.order_key : orderKeyBetweenTasks(before, after);
}
